import React, { useCallback } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';

import { Delete } from '@mui/icons-material';
import { Box, styled, TableCell, TableRow, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { RowItems } from 'models/TableProps';

import ProfitMarginControlled from 'components/shared/ProfitMarginControlled';
import QuantityInputControlled from 'components/shared/QuantityInputControlled';

import { formatPrice } from 'utils/numberFormatter';

import type {
    CustomItem,
    QuotationProductsForm,
    QuotationProductsUpdateProps,
} from 'models/quotations/Quotation';
import type { QuotationProductItem } from 'models/quotations/Quotations';

import useConfirmDialog from 'hooks/dialogs/useConfirmDialog';
import { CustomItemsWrapper } from '../shared/CustomItemsWrapper';

const StyledImage = styled(Box)(() => ({
    width: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    img: {
        maxWidth: '100%',
    },
}));

const ProductTableCell = styled(TableCell)(() => ({
    borderBottom: 'none',
    padding: '12px 6px',
}));

const ProductTableRow = styled(TableRow)(() => ({
    borderBottom: '1px solid #DAE1E7',
    // backgroundColor: '#f4f4f4',
    margin: '24px 0',
}));
const StyledQtySelect = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledDelete = styled(Delete)(() => ({
    borderRadius: '50%',
    padding: '4px',
    cursor: 'pointer',
    fontSize: '30px',
    ':hover': {
        backgroundColor: '#ffdede',
    },
}));

const CustomItemsContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
}));

const Row = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'center',
}));

export const QuotationProductsTableRow: React.FC<
    RowItems<QuotationProductItem>
> = ({ index, handleEvent, handleChange }) => {
    const { t } = useTranslation('quotations', {
        keyPrefix:
            'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_PRODUCTS.ROW_CONTENT',
    });

    const { t: tCustom } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.CUSTOM_ITEMS.FIELDS',
    });

    const { control, watch } = useFormContext<QuotationProductsForm>();

    const { remove: removeCustom, append: appendCustom } = useFieldArray({
        control,
        name: `items.${index}.customs`,
    });

    const item = watch(`items.${index}`);

    const confirmDialog = useConfirmDialog();

    const handleDelete = useCallback((id) => {
        confirmDialog.showDialog({
            title: t('DELETE_MODAL.TITLE'),
            content: t('DELETE_MODAL.CONTENT'),
            confirmText: t('DELETE_MODAL.CONFIRM'),
            cancelText: t('DELETE_MODAL.CANCEL'),
            onConfirm: handleEvent.bind(this, { index, quoteItemId: item.id }),
        });
    }, []);

    const handleAddCustom = useCallback(
        () =>
            appendCustom({
                name: '',
                net_price: 0,
                tax: 0,
                quantity: 1,
            }),
        []
    );

    const handleCustomItemsChange = (content: CustomItem[]) => {
        handleChange<QuotationProductsUpdateProps>({
            index: index,
            id: item.id,
            item: {
                id: item.id,
                customs: content,
            },
        });
    };

    return (
        <>
            <ProductTableRow sx={{ borderBottom: 'none' }}>
                <ProductTableCell sx={{ width: '90px' }}>
                    <StyledImage>
                        <img src={item.image} alt={item.name} />
                    </StyledImage>
                </ProductTableCell>

                <ProductTableCell>
                    <Typography
                        variant="caption"
                        style={{
                            maxWidth: '200px',
                        }}
                    >
                        {item.name}
                    </Typography>
                </ProductTableCell>

                <ProductTableCell>
                    <StyledQtySelect
                        sx={{
                            maxWidth: '200px',
                            minWidth: '120px',
                            gap: '12px',
                        }}
                    >
                        <QuantityInputControlled
                            control={control}
                            name={`items.${index}.quantity`}
                            minOrderMultiplier={!!item.min_order_multiplier}
                            minOrderQuantity={item.min_order_quantity ?? 1}
                            flat
                        />
                        <Typography>{t('QUANTITY_UNITS')}</Typography>
                    </StyledQtySelect>
                </ProductTableCell>

                <ProductTableCell
                    sx={{ minWidth: '100px', justifyContent: 'flex-end' }}
                >
                    <Typography sx={{ whiteSpace: 'nowrap' }} textAlign="end">
                        {formatPrice(item.net_price)} Ft
                    </Typography>
                </ProductTableCell>

                <ProductTableCell sx={{ maxWidth: '300px', minWidth: '200px' }}>
                    <ProfitMarginControlled
                        control={control}
                        name={`items.${index}.margin`}
                        net_price={item.net_price}
                        orientation="row"
                    />
                </ProductTableCell>

                <ProductTableCell
                    sx={{ minWidth: '100px', justifyContent: 'flex-end' }}
                >
                    <Typography sx={{ whiteSpace: 'nowrap' }} textAlign="end">
                        {formatPrice(
                            (Number(item.net_price) + Number(item.margin)) *
                                item.quantity
                        )}{' '}
                        Ft
                    </Typography>
                </ProductTableCell>

                <ProductTableCell>
                    <StyledDelete color="error" onClick={handleDelete} />
                </ProductTableCell>
            </ProductTableRow>

            {item.customs && (
                <ProductTableRow sx={{ borderBottom: 'none' }}>
                    <ProductTableCell />
                    <ProductTableCell colSpan={6}>
                        <CustomItemsWrapper
                            fieldsVariant="outlined"
                            defaultCustomItems={item.customs}
                            handleChange={handleCustomItemsChange}
                            origin="products"
                        />
                    </ProductTableCell>
                </ProductTableRow>
            )}

            {/* <ProductTableRow>
                <ProductTableCell />
                <ProductTableCell colSpan={5}>
                    <Link
                        sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                        }}
                        variant="small"
                        onClick={handleAddCustom}
                        textTransform="uppercase"
                    >
                        {t('ADD_NEW')}
                    </Link>
                </ProductTableCell>
            </ProductTableRow> */}
        </>
    );
};
